import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useState} from 'react';
import {Mutation, withApollo} from 'react-apollo';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import {PulseLoader} from 'react-spinners';
import {checkErrorType} from '../../../utils';
import Error from '../../../common/components/Error/Error';
import PopIn from '../../../common/components/PopIn';
import SelectMerchant from '../../../common/components/SelectMerchant';
import {CancelButton, Div, FooterRow, GradientCol, Input, TitleButton} from '../../../common/components/StyledComp';
import Tooltip from '../../../common/components/Tooltip';
import {ERROR_TYPE_CHECKOUT_EXISTS, ROLES} from '../../../common/constants';
import {withLoadedUser} from '../../../login/containers/AuthBase';
import {GRAPHQL_MUTATION_ADD_CHECKOUT} from '../../../common/queries/checkout';

const AddCheckout = ({t, currentUser: {role}}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [newCheckout, setNewCheckout] = useState({serial: null, name: null, merchantId: null});

  const onCheckoutAdded = addCheckout => {
    addCheckout().then(({data}) => {
      if (data.addCheckout) {
        setModalVisible(false);
        setNewCheckout({serial: null, name: null, merchantId: null});
      }
    });
  };

  if (role !== ROLES.ADMIN) {
    return null;
  }

  return (
    <Mutation
      mutation={GRAPHQL_MUTATION_ADD_CHECKOUT}
      variables={{checkoutInput: newCheckout}}
      onCompleted={() => {
        setSubmitted(false);
      }}
      refetchQueries={['checkouts']}
    >
      {(addCheckout, {error, loading}) => {
        let errorDisplay = error;

        if (error && checkErrorType(error, ERROR_TYPE_CHECKOUT_EXISTS)) {
          errorDisplay = t(ERROR_TYPE_CHECKOUT_EXISTS);
        }

        return (
          <div>
            <TitleButton onClick={() => setModalVisible(true)} className="title-btn">
              {t('addCheckout.title')}
            </TitleButton>

            <PopIn visible={modalVisible} setModalVisible={setModalVisible} title={t('addCheckout.title')}>
              <Container>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    if (newCheckout.merchantId >= 0 && newCheckout.name) {
                      onCheckoutAdded(addCheckout);
                    }
                  }}
                >
                  <Row>
                    <Col xs={12}>
                      <Div className="attribute-name">{`${t('merchantName')} : `}</Div>
                      <SelectMerchant
                        onChange={selectOption => {
                          setNewCheckout({...newCheckout, merchantId: selectOption.value});
                          setSubmitted(false);
                        }}
                      />
                      <Tooltip show={!newCheckout.merchantId && submitted} color="red">
                        {t('addCheckout.requiredField')}
                      </Tooltip>
                    </Col>
                    <Col xs={12}>
                      <Div className="attribute-name">{`${t('name')} : `}</Div>
                      <Input
                        type="text"
                        placeholder={t('name')}
                        name="name"
                        onChange={e => {
                          setNewCheckout({...newCheckout, name: e.target.value});
                          setSubmitted(false);
                        }}
                        empty={!newCheckout.name && submitted}
                      />
                      <Tooltip show={!newCheckout.name && submitted} color="red">
                        {t('addCheckout.requiredField')}
                      </Tooltip>
                    </Col>
                    <Col xs={12}>
                      <Div className="attribute-name">{`${t('serial')} : `}</Div>
                      <Input
                        type="text"
                        placeholder={t('serial')}
                        name="serial"
                        onChange={e => {
                          setNewCheckout({...newCheckout, serial: e.target.value});
                          setSubmitted(false);
                        }}
                      />
                    </Col>
                  </Row>
                  <FooterRow>
                    <GradientCol sm={{span: 6, offset: 3}}>
                      <Error error={errorDisplay} />
                      <PulseLoader loading={loading} />
                      <div className="popin-buttons">
                        <TitleButton
                          type="submit"
                          disabled={loading}
                          onClick={() => {
                            setSubmitted(true);
                          }}
                        >
                          {t('addCheckout.title')}
                        </TitleButton>
                        <CancelButton
                          onClick={() => {
                            setModalVisible(false);
                            setNewCheckout({serial: ''});
                            setSubmitted(false);
                          }}
                          style={{marginTop: 0, width: 'auto'}}>
                          {t('cancel')}
                        </CancelButton>
                      </div>
                    </GradientCol>
                  </FooterRow>
                </form>
              </Container>
            </PopIn>
          </div>
        );
      }}
    </Mutation>
  );
};

AddCheckout.propTypes = {
  t: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string.isRequired,
  }).isRequired,
};

export default compose(
  withApollo,
  withTranslation('checkout'),
  withLoadedUser,
)(AddCheckout);
